'use client'

import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import type { OrgSchemaType } from '@/types/portal-api.generated'
import type { Member } from '@stytch/nextjs/b2b'
import { useStytchMember } from '@stytch/nextjs/b2b'
import { identifyUser } from '@/utilities/analytics/userAnalytics'

interface UserContextType {
  user: Member | null
  organization: OrgSchemaType | null
  setUser: (user: Member | null) => void
  setOrganization: (organization: OrgSchemaType | null) => void
  clearUserContext: () => void
}

const UserContext = createContext<UserContextType | undefined>(undefined)

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<Member | null>(null)
  const [organization, setOrganization] = useState<OrgSchemaType | null>(null)
  const lastIdentifiedUser = useRef<string | 'anonymous' | null>(null)

  // Auto-detect existing Stytch sessions
  const { member, isInitialized } = useStytchMember()

  useEffect(() => {
    // when users are not logged in yet, identify user as anonymous

    if (!isInitialized) return
    if (member) {
      if (lastIdentifiedUser.current !== member.member_id) {
        identifyUser(member)
        lastIdentifiedUser.current = member.member_id
      }
      return
    }

    if (lastIdentifiedUser.current !== 'anonymous') {
      identifyUser(undefined)
      lastIdentifiedUser.current = 'anonymous'
    }
  }, [isInitialized, member])

  useEffect(() => {
    // when closing the tab and/or opening a new tab, we'd need to repopulate the user context using the Stytch Provider
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- event work runs in an effect; keep until refactor
    if (isInitialized && member && !user) {
      // eslint-disable-next-line react-hooks/set-state-in-effect, react-you-might-not-need-an-effect/no-chain-state-updates -- state update required inside this async/side-effect; cannot be derived; chained state updates in an effect; keep until refactor
      setUser(member)

      if (member.organization_id) {
        const organization = {
          id: member.organization_id,
          name: '', // Not available from member directly
          slug: '', // Not available from member directly
          organizationId: member.organization_id,
          primaryProgramId: '', // Not available from Stytch
          roles: [], // Not available from Stytch
          scopes: {}, // Not available from Stytch
        }
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates -- chained state updates in an effect; keep until refactor
        setOrganization(organization)
      } //
    }
  }, [member, isInitialized, user])

  const clearUserContext = () => {
    setUser(null)
    setOrganization(null)
  }

  const value: UserContextType = {
    user,
    setUser,
    organization,
    setOrganization,
    clearUserContext,
  }

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

export function useUser() {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}
